import React, { useState, useMemo, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";
import { Trash2, Save, Plus, FileText, Search, User as UserIcon, Warehouse, Calendar as CalendarIcon, Globe, Info, ShieldCheck } from "lucide-react";

import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { CustomDateInput } from "../../../components/CustomDateInput";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { useCustomerStore } from "../../../store/customerStore";
import { useProductStore } from "../../../store/productStore";
import { useWarehouseStore } from "../../../store/warehouse";
import { toast } from "react-toastify";
import { Button } from "primereact/button";
import { useCurrencyStore } from "../../../store/currencyStore";
import { Dropdown } from "primereact/dropdown";
import api from "../../../service/api";
import { RollTransactionModal } from "../../../hooks/RollTransactionModal";

interface InvoiceDialogProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'PURCHASE' | 'SALES';
    onSave: (data: any) => void;
    initialData?: any;
}

export const InvoiceDialog: React.FC<InvoiceDialogProps> = ({ isOpen, onClose, type, onSave, initialData }) => {
    const { fetchDataDynamic: fetchCustomers } = useCustomerStore();
    const { fetchDataDynamic: fetchStocks } = useProductStore();
    const { fetchDataDynamic: fetchWarehouses } = useWarehouseStore();
    const { getLiveRates } = useCurrencyStore();
    const customerSelect = useSelectDialog<any>();
    const stockSelect = useSelectDialog<any>();
    const warehouseSelect = useSelectDialog<any>();

    const [isRollModalOpen, setIsRollModalOpen] = useState(false);
    const [selectedItemForRolls, setSelectedItemForRolls] = useState<any>(null);

    const [invoice, setInvoice] = useState({
        accountId: null as number | null,
        accountName: "",
        vknTckn: "",
        warehouseId: null as number | null,
        warehouseName: "",
        date: new Date(),
        dueDate: new Date(),
        reference: "",
        description: "",
        currency: "TRY",
        exchangeRate: 1,
        items: [] as any[],
        // --- e-Fatura / NES Alanları ---
        eInvoiceProfile: "TEMELFATURA",
        eInvoiceTypeCode: "SATIS",
        eArchiveSendType: "ELEKTRONIK",
        isInternetSale: false,
        isDespatch: true,
        internetWebSite: "",
        paymentMethod: "KREDIKARTI/BANKAKARTI",
        paymentDate: new Date(),
        transporterName: "",
    });

    const checkEInvoiceUser = async (vkn: string) => {
        if (!vkn || vkn.length < 10 || type === 'PURCHASE') return;
        try {
            const res = await api.get(`/firm-integration/check-user/${vkn}`);
            if (res.data?.isEInvoiceUser) {
                setInvoice(prev => ({ ...prev, eInvoiceProfile: "TICARIFATURA" }));
                toast.info("Cari e-Fatura mükellefidir.");
            } else {
                setInvoice(prev => ({ ...prev, eInvoiceProfile: "EARSIVFATURA" }));
            }
        } catch (error) {
            setInvoice(prev => ({ ...prev, eInvoiceProfile: "EARSIVFATURA" }));
        }
    };

    useEffect(() => {
        const updateRate = async () => {
            if (!initialData && isOpen && invoice.currency !== 'TRY') {
                try {
                    const rates = await getLiveRates();
                    const currencyKey = invoice.currency as keyof typeof rates;
                    const newRate = rates[currencyKey] || 1;
                    setInvoice(prev => ({ ...prev, exchangeRate: newRate }));
                } catch (error) { console.error("Kur çekilemedi:", error); }
            } else if (invoice.currency === 'TRY') {
                setInvoice(prev => ({ ...prev, exchangeRate: 1 }));
            }
        };
        updateRate();
    }, [invoice.currency, isOpen, initialData]);

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setInvoice({
                    ...initialData,
                    accountId: initialData.accountId,
                    accountName: initialData.account?.name || "",
                    warehouseId: initialData.stockMovements?.[0]?.warehouseId || null,
                    warehouseName: initialData.stockMovements?.[0]?.warehouse?.name || "Varsayılan Depo",
                    date: new Date(initialData.date),
                    dueDate: new Date(initialData.dueDate || initialData.date),
                    reference: initialData.reference || "",
                    description: initialData.description || "",
                    currency: initialData.currency || "TRY",
                    exchangeRate: initialData.exchangeRate || 1,
                    items: initialData.stockMovements?.map((sm: any) => ({
                        id: sm.id,
                        stockId: sm.stockId,
                        variantId: sm.variantId,
                        stockName: sm.variantId ? `${sm.stock?.name} (${sm.variant?.name})` : sm.stock?.name,
                        quantity: Math.abs(sm.quantity),
                        unitPrice: sm.unitPrice,
                        taxRate: sm.taxRate,
                        discountRate: sm.discountRate || 0,
                        rollTransactions: sm.rollTransactions || []
                    })) || []
                });
            } else {
                setInvoice(prev => ({
                    ...prev, accountId: null, accountName: "", items: [], date: new Date(),
                    eInvoiceProfile: "EARSIVFATURA", reference: "", description: ""
                }));
                fetchWarehouses({ pageIndex: 1, pageSize: 5 }).then(res => {
                    if (res?.items?.length > 0) {
                        setInvoice(prev => ({ ...prev, warehouseId: res.items[0].id, warehouseName: res.items[0].name }));
                    }
                });
            }
        }
    }, [isOpen, initialData]);

    const summary = useMemo(() => {
        let base = 0, tax = 0, discount = 0;
        invoice.items.forEach(item => {
            const lineBase = (item.quantity * item.unitPrice);
            const lineDiscount = lineBase * (item.discountRate / 100);
            const lineTax = (lineBase - lineDiscount) * (item.taxRate / 100);
            base += lineBase; discount += lineDiscount; tax += lineTax;
        });
        const total = base - discount + tax;
        return { base, tax, discount, total, totalTRY: total * invoice.exchangeRate };
    }, [invoice.items, invoice.exchangeRate]);

    const handleSave = () => {
        if (!invoice.accountId) { toast.error("Lütfen bir cari hesap seçiniz."); return; }
        if (invoice.items.length === 0) { toast.error("Faturaya en az bir ürün eklemelisiniz."); return; }
        if (!invoice.warehouseId) { toast.error("Lütfen bir depo seçiniz."); return; }

        const payload = {
            id: initialData?.id,
            // 🔥 accountId'yi buradan ÇIKARDIK çünkü backend'de 'account: { connect: { id: body.accountId } }' 
            // şeklinde kullanılıyor. DTO'da accountId kalabilir ama Prisma'ya giderken çakışma yaratıyor.
            // Controller koduna baktığımızda body'den accountId'yi okuyor. 
            // Ama Prisma create içine ...movementData ile girdiği için hata veriyor.

            accountId: invoice.accountId, // DTO için gerekli
            linkedWaybillIds: initialData?.linkedWaybillIds,
            type: "INVOICE",
            invoiceType: type === 'SALES' ? 'SALES' : 'PURCHASE',
            direction: type === 'SALES' ? 'OUT' : 'IN',
            currency: invoice.currency,
            exchangeRate: invoice.exchangeRate,
            amount: summary.total,
            baseAmount: summary.base,
            taxAmount: summary.tax,
            discountAmount: summary.discount,
            reference: invoice.reference,
            description: invoice.description,
            date: invoice.date,
            dueDate: invoice.dueDate,
            eInvoiceProfile: invoice.eInvoiceProfile,
            eInvoiceTypeCode: invoice.eInvoiceTypeCode,
            isInternetSale: invoice.isInternetSale,
            isDespatch: invoice.isDespatch,
            ...(invoice.isInternetSale && { internetWebSite: invoice.internetWebSite || "" }),

            stockMovements: invoice.items.map(item => {
                const movement: any = {
                    stockId: item.stockId,
                    warehouseId: invoice.warehouseId,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    taxRate: item.taxRate,
                    discountRate: item.discountRate,
                    totalAmount: (item.quantity * item.unitPrice) * (1 - item.discountRate / 100) * (1 + item.taxRate / 100),
                    type: type === 'SALES' ? 'OUT' : 'IN'
                };
                if (item.rollTransactions && item.rollTransactions.length > 0) {
                    movement.rollTransactions = item.rollTransactions;
                }

                if (item.variantId && item.variantId !== 0) {
                    movement.variantId = Number(item.variantId);
                }

                return movement;
            })
        };
        onSave(payload);
    };

    const addLine = async () => {
        const res = await fetchStocks({ pageIndex: 1, pageSize: 50 });
        const selected = await stockSelect.open({
            title: "Ürün / Varyant Seç",
            data: res.items,
            columns: [
                { field: "sku", header: "SKU" },
                { field: "name", header: "Ürün Adı" },
                { field: "quantity", header: "Stok" }
            ],
            rowExpansionTemplate: (stock: any) => (
                <div className="p-3">
                    <DataTable value={stock.variants} className="p-datatable-sm shadow-1 rounded-lg">
                        <Column field="name" header="Varyant" />
                        <Column field="sku" header="Barkod" />
                        <Column body={(variant) => (
                            <Button
                                type="button" label="Seç" icon="pi pi-check" className="p-button-sm p-button-info"
                                onClick={(e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    stockSelect.selectItem({ ...stock, selectedVariant: variant });
                                }}
                            />
                        )} />
                    </DataTable>
                </div>
            )
        });

        if (selected) {
            const isVariant = !!selected.selectedVariant;
            const defaultPrice = selected.prices?.length > 0
                ? (type === 'SALES' ? selected.prices[0].salePrice : selected.prices[0].purchasePrice)
                : (type === 'SALES' ? (selected.salePrice || 0) : (selected.purchasePrice || 0));

            const newItem = {
                id: Math.random().toString(36).substr(2, 9),
                stockId: isVariant ? selected.selectedVariant.stockId : selected.id,
                variantId: isVariant ? selected.selectedVariant.id : null,
                stockName: isVariant ? `${selected.name} (${selected.selectedVariant.name})` : selected.name,
                quantity: 1,
                unitPrice: defaultPrice,
                taxRate: selected.taxRate || 20,
                discountRate: 0,
                rollTransactions: []
            };
            setInvoice(prev => ({ ...prev, items: [...prev.items, newItem] }));
        }
    };

    const openRollsDialog = (row: any) => {
        if (!invoice.warehouseId) { toast.error("Lütfen önce bir depo seçiniz."); return; }
        setSelectedItemForRolls(row);
        setIsRollModalOpen(true);
    };

    const handleRollsComplete = (updatedRolls?: any[]) => {
        if (updatedRolls && selectedItemForRolls) {
            const totalQuantity = updatedRolls.reduce((acc, curr) => acc + (parseFloat(curr.quantity) || 0), 0);
            setInvoice(prev => ({
                ...prev,
                items: prev.items.map(i => i.id === selectedItemForRolls.id ? { ...i, quantity: totalQuantity, rollTransactions: updatedRolls } : i)
            }));
        }
        setIsRollModalOpen(false);
        setSelectedItemForRolls(null);
    };

    const currencyOptions = [
        { label: 'Türk Lirası', value: 'TRY', icon: '₺' },
        { label: 'Amerikan Doları', value: 'USD', icon: '$' },
        { label: 'Euro', value: 'EUR', icon: '€' },
    ];

    const currencyTemplate = (option: any) => (
        <div className="flex justify-between items-center w-full">
            <span className="font-bold">{option.value}</span>
            <span className="text-xs">{option.icon}</span>
        </div>
    );

    return (
        <Dialog
            maximizable resizable={false}
            header={
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${type === 'SALES' ? 'sales-icon-bg' : 'purchase-icon-bg'}`}>
                        <FileText size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold leading-none">
                            {initialData ? 'Faturayı Düzenle' : (type === 'SALES' ? 'Yeni Satış Faturası' : 'Yeni Alış Faturası')}
                        </h2>
                        <p className="text-xs mt-1">{initialData ? `${invoice.reference} düzenleniyor` : 'Yeni belge oluşturuluyor...'}</p>
                    </div>
                </div>
            }
            visible={isOpen} onHide={onClose} maximized
            footer={
                <div className="flex flex-col md:flex-row justify-between items-center w-full gap-4 p-2  text-text">
                    <div className="flex flex-wrap justify-around md:justify-start items-center gap-3 md:gap-12 w-full md:w-auto">
                        <div className="flex flex-col items-center md:items-start">
                            <span className="text-[10px] uppercase font-bold mb-1">Ara Toplam</span>
                            <span className="text-md md:text-lg font-semibold">
                                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: invoice.currency }).format(summary.base)}
                            </span>
                        </div>
                        <div className="flex flex-col items-center md:items-start">
                            <span className="text-[10px] uppercase font-bold mb-1">KDV Toplamı</span>
                            <span className="text-md md:text-lg font-bold">
                                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: invoice.currency }).format(summary.tax)}
                            </span>
                        </div>
                        <div className="flex flex-col items-center md:items-start">
                            <span className="text-[10px] uppercase font-bold mb-1">Genel Toplam</span>
                            <div className="flex flex-col">
                                <span className="text-md md:text-lg font-black text-primary">
                                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: invoice.currency }).format(summary.total)}
                                </span>
                                {invoice.currency !== 'TRY' && (
                                    <span className="text-[10px] font-bold">TL Karş: {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(summary.totalTRY)}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3 w-full md:w-auto mt-2 md:mt-0">
                        <TextButton onClick={onClose} className="btn-cancel">Vazgeç</TextButton>
                        <TextButton onClick={handleSave} className="btn-new flex gap-3">
                            <Save size={20} /><span>Faturayı Kaydet</span>
                        </TextButton>
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-4 p-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    <div className="flex flex-col gap-1.5">
                        <CustomSelectInput
                            label={<><UserIcon size={12} /> Cari Hesap</>}
                            value={invoice.accountName || ""}
                            placeholder="Cari Seçiniz..."
                            icon={Search}
                            onClick={async () => {
                                const res = await fetchCustomers({ pageIndex: 1, pageSize: 20 });
                                const selected = await customerSelect.open({ title: "Cari Seç", data: res.items, columns: [{ field: "name", header: "Cari Adı" }, { field: "taxNumber", header: "VKN" }] });
                                if (selected) {
                                    setInvoice(prev => ({ ...prev, accountId: selected.id, accountName: selected.name, vknTckn: selected.taxNumber }));
                                    checkEInvoiceUser(selected.taxNumber);
                                }
                            }}
                            onClear={() => setInvoice(prev => ({ ...prev, accountId: 0, accountName: "", vknTckn: "", isEInvoice: false }))}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <CustomSelectInput
                            label={<><Warehouse size={12} /> Depo</>}
                            value={invoice.warehouseName || ""}
                            placeholder="Depo Seçiniz..."
                            icon={Warehouse}
                            onClick={async () => {
                                const res = await fetchWarehouses({ pageIndex: 1, pageSize: 10 });
                                const selected = await warehouseSelect.open({ title: "Depo Seç", data: res.items, columns: [{ field: "name", header: "Depo Adı" }] });
                                if (selected) setInvoice(prev => ({ ...prev, warehouseId: selected.id, warehouseName: selected.name }));
                            }}
                            onClear={() => setInvoice(prev => ({ ...prev, warehouseId: 0, warehouseName: "" }))}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <CustomDateInput
                            label={<><CalendarIcon size={12} /> Tarih</>}
                            value={invoice.date}
                            onChange={(val) => setInvoice(prev => ({ ...prev, date: val as Date }))}
                            selectionMode="single"
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-bold uppercase flex items-center gap-1"><Globe size={12} /> Döviz / Kur</label>
                        <div className="flex gap-1 h-10">
                            <Dropdown
                                value={invoice.currency} options={currencyOptions}
                                onChange={(e) => setInvoice(prev => ({ ...prev, currency: e.value }))}
                                className="w-1/2 flex items-center font-bold text-sm "
                                itemTemplate={currencyTemplate}
                            />
                            <div className="relative flex-1">
                                <InputNumber
                                    value={invoice.exchangeRate}
                                    onValueChange={(e) => setInvoice(prev => ({ ...prev, exchangeRate: e.value || 1 }))}
                                    mode="decimal" minFractionDigits={2} maxFractionDigits={4}
                                    disabled={invoice.currency === 'TRY'} className="w-full h-full"
                                    inputClassName="w-full h-full font-mono text-xs text-right pr-2"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-bold uppercase flex items-center gap-1"><Info size={12} /> Açıklama</label>
                        <InputText value={invoice.description} onChange={(e) => setInvoice(prev => ({ ...prev, description: e.target.value }))} placeholder="Not..." className="h-10" />
                    </div>
                </div>

                {/* 🔥 e-Fatura / NES Ayarlar Paneli */}
                {type === 'SALES' && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-3  card e-invoice-panel">
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold uppercase flex items-center gap-1 text-primary"><ShieldCheck size={12} /> Fatura Senaryosu</label>
                            <Dropdown value={invoice.eInvoiceProfile} options={['TEMELFATURA', 'TICARIFATURA', 'EARSIVFATURA']} onChange={(e) => setInvoice(p => ({ ...p, eInvoiceProfile: e.value }))} className="p-inputtext-sm" />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold uppercase">Fatura Tipi</label>
                            <Dropdown value={invoice.eInvoiceTypeCode} options={['SATIS', 'IADE', 'TEVKIFAT', 'ISTISNA']} onChange={(e) => setInvoice(p => ({ ...p, eInvoiceTypeCode: e.value }))} className="p-inputtext-sm" />
                        </div>
                        <div className="flex items-center gap-4 pt-4">
                            <div className="flex items-center gap-2">
                                <InputSwitch checked={invoice.isDespatch} onChange={(e) => setInvoice(p => ({ ...p, isDespatch: e.value }))} />
                                <label className="text-[10px] font-bold uppercase">İrsaliye Yerine Geçer</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <InputSwitch checked={invoice.isInternetSale} onChange={(e) => setInvoice(p => ({ ...p, isInternetSale: e.value }))} />
                                <label className="text-[10px] font-bold uppercase">İnternet Satışı</label>
                            </div>
                        </div>
                        {invoice.isInternetSale && (
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold uppercase">Web Sitesi</label>
                                <InputText value={invoice.internetWebSite} onChange={(e) => setInvoice(p => ({ ...p, internetWebSite: e.target.value }))} placeholder="www.site.com" className="h-8 text-xs" />
                            </div>
                        )}
                    </div>
                )}

                <div className="flex-1 rounded-lg shadow-sm overflow-hidden flex flex-col">
                    <div className="p-4 card2 rounded-b-none flex-col sm:flex-row flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <span className="font-bold">Fatura Kalemleri</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold border">{invoice.items.length} Kalem</span>
                        </div>
                        <TextButton onClick={addLine} className="btn-new flex gap-3 w-full sm:w-auto"><Plus />Satır Ekle</TextButton>
                    </div>
                    <div className="flex-1 min-h-[300px]">
                        <DataTable
                            value={invoice.items} className="p-datatable-sm flex-1 rounded-t-none" rowHover
                            emptyMessage={<div className="p-4 text-center">Ürün eklenmedi.</div>}
                        >
                            <Column
                                header="ÜRÜN / VARYANT" style={{ minWidth: '250px' }}
                                body={(row) => (
                                    <div className="flex flex-col gap-1">
                                        <span className="font-semibold">{row.stockName}</span>
                                        {row.variantId && <span className="variant-badge text-[10px] px-2 py-0.5   w-fit font-bold uppercase">Varyant</span>}
                                    </div>
                                )}
                            />
                            <Column header="MİKTAR / TOPLAR" style={{ width: '180px' }} body={(row) => (
                                <div className="flex items-center gap-2">
                                    <InputNumber value={row.quantity} onValueChange={(e) => setInvoice(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, quantity: e.value } : i) }))} disabled={row.rollTransactions?.length > 0} buttonLayout="horizontal" className="w-20" inputClassName="w-12 text-center font-bold" min={1} />
                                    <Button type="button" label={row.rollTransactions?.length > 0 ? `${row.rollTransactions.length} Top` : "Top"} icon="pi pi-barcode" className={`p-button-sm px-2 py-1 ${row.rollTransactions?.length > 0 ? 'p-button-success' : 'p-button-outlined'}`} onClick={() => openRollsDialog(row)} />
                                </div>
                            )} />
                            <Column header="BİRİM FİYAT" style={{ width: '150px' }} body={(row) => (
                                <InputNumber
                                    value={row.unitPrice} onValueChange={(e) => setInvoice(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, unitPrice: e.value } : i) }))}
                                    mode="currency" currency={invoice.currency} inputClassName="w-full font-bold text-right"
                                />
                            )} />
                            <Column header="KDV (%)" style={{ width: '100px' }} body={(row) => (
                                <InputNumber value={row.taxRate} onValueChange={(e) => setInvoice(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, taxRate: e.value } : i) }))} prefix="%" inputClassName="w-full text-center" />
                            )} />
                            <Column header="İSK (%)" style={{ width: '100px' }} body={(row) => (
                                <InputNumber value={row.discountRate} onValueChange={(e) => setInvoice(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, discountRate: e.value } : i) }))} prefix="%" inputClassName="w-full text-center" />
                            )} />
                            <Column header="TOPLAM" align="right" className="font-bold" style={{ width: '150px' }} body={(row) => {
                                const total = (row.quantity * row.unitPrice) * (1 - row.discountRate / 100) * (1 + row.taxRate / 100);
                                return <span>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: invoice.currency }).format(total)}</span>
                            }} />
                            <Column style={{ width: '50px' }} body={(row) => (
                                <IconButton icon={Trash2} onClick={() => setInvoice(prev => ({ ...prev, items: prev.items.filter(i => i.id !== row.id) }))} className="text-red-400" />
                            )} />
                        </DataTable>
                    </div>
                </div>
            </div>
            {customerSelect.modal} {stockSelect.modal} {warehouseSelect.modal}

            {selectedItemForRolls && (
                <RollTransactionModal
                    isOpen={isRollModalOpen}
                    onClose={handleRollsComplete}
                    type={type === 'SALES' ? 'OUT' : 'IN'}
                    initialRolls={selectedItemForRolls.rollTransactions || []}
                    stockId={selectedItemForRolls.stockId}
                    warehouseId={invoice.warehouseId!}
                    variantId={selectedItemForRolls.variantId}
                />
            )}
        </Dialog>
    );
};